import type {
  LogMethod,
  CreateMethodDto,
  UpdateMethodDto,
} from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

interface MethodState {
  methods: LogMethod[];
  status: StateStatus;
}

interface MethodActions {
  fetchMethods: (workspaceId: number) => Promise<void>;
  createMethod: (
    workspaceId: number,
    dto: CreateMethodDto,
  ) => Promise<LogMethod>;
  updateMethod: (
    workspaceId: number,
    methodId: number,
    dto: UpdateMethodDto,
  ) => Promise<LogMethod>;
  deleteMethod: (workspaceId: number, methodId: number) => Promise<void>;
  reset: () => void;
}

export type MethodStore = MethodState & MethodActions;

const initialState: MethodState = {
  methods: [],
  status: "idle",
};

export function createMethodStore(deps: StoreDeps) {
  return createStore<MethodStore>("MethodStore", (set) => ({
    ...initialState,

    fetchMethods: (workspaceId) =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const methods = await deps.api.method.list(workspaceId);
          set((state) => { state.methods = methods; });
        },
      ),

    createMethod: async (workspaceId, dto) => {
      const method = await deps.api.method.create(workspaceId, dto);
      set((state) => { state.methods.push(method); });
      return method;
    },

    updateMethod: async (workspaceId, methodId, dto) => {
      const method = await deps.api.method.update(workspaceId, methodId, dto);
      set((state) => {
        const idx = state.methods.findIndex((m) => m.id === methodId);
        if (idx !== -1) state.methods[idx] = method;
      });
      return method;
    },

    deleteMethod: async (workspaceId, methodId) => {
      await deps.api.method.remove(workspaceId, methodId);
      set((state) => {
        state.methods = state.methods.filter((m) => m.id !== methodId);
      });
    },

    reset: createReset(set as ImmerSet<MethodState>, initialState),
  }));
}
